import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { AvianLogo } from "../components/AvianLogo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "Cape Town", role: "customer" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) {
      toast.success("Account created. Welcome to tadada!");
      navigate(r.user?.role === "driver" ? "/driver" : r.user?.role === "owner" ? "/owner" : "/");
    } else {
      toast.error(r.error || "Could not create account");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-8 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Get started</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#08153a] mt-2">Create your account</h2>
          <p className="text-sm text-[#64748B] mt-2">Already have one? <Link to="/login" className="text-[#1d7ffd] font-semibold hover:underline">Log in</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <Field label="Full name">
              <input data-testid="reg-name" required value={form.name} onChange={set("name")} className="ip" placeholder="Lerato Khumalo" />
            </Field>
            <Field label="Email">
              <input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="ip" placeholder="you@example.com" />
            </Field>
            <Field label="Password">
              <div className="relative">
                <input data-testid="reg-password" type={show ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} className="ip pr-12" placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#08153a]">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="ip" placeholder="+27 71 555 0000" />
              </Field>
              <Field label="City">
                <input data-testid="reg-city" value={form.city} onChange={set("city")} className="ip" placeholder="Cape Town" />
              </Field>
            </div>
            <Field label="I want to">
              <div className="grid grid-cols-3 gap-2">
                <RoleCard active={form.role === "customer"} onClick={() => setForm((f) => ({ ...f, role: "customer" }))} title="Rent cars" desc="Find and book" testid="reg-role-customer" />
                <RoleCard active={form.role === "owner"} onClick={() => setForm((f) => ({ ...f, role: "owner" }))} title="List my car" desc="Earn as a host" testid="reg-role-owner" />
                <RoleCard active={form.role === "driver"} onClick={() => setForm((f) => ({ ...f, role: "driver" }))} title="Drive" desc="Earn as a chauffeur" testid="reg-role-driver" />
              </div>
              {form.role === "driver" && (
                <p className="text-xs text-[#78350F] bg-[#FFFBEB] border border-[#F59E0B]/40 rounded-xl px-3 py-2 mt-2" data-testid="reg-driver-docs-note">
                  Chauffeurs must verify an ID, driver's licence and a SAPS police clearance certificate (issued within the last 6 months) before accepting trips.
                </p>
              )}
              <div className="hidden">
              </div>
            </Field>

            <button data-testid="register-submit" disabled={loading} className="btn-primary w-full disabled:opacity-60 mt-2">
              {loading ? "Creating…" : (<>Create account <ArrowRight size={16} /></>)}
            </button>
            <p className="text-[11px] text-[#64748B] text-center">By signing up you agree to tadada's Terms & Privacy.</p>
          </form>

          <style>{`.ip{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:12px 16px;font-size:14px;color:#08153a;outline:none;transition:border-color .2s}.ip:focus{border-color:#1d7ffd}`}</style>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/8631623/pexels-photo-8631623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900" alt="Drive" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#08153a]/85 via-[#08153a]/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="bg-white rounded-2xl inline-block p-3 self-end"><AvianLogo size={26} /></div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-3">Join the marketplace</p>
            <h1 className="font-display text-4xl font-black leading-tight max-w-md">Trusted cars. Local hosts. The freedom to go anywhere.</h1>
            <div className="mt-8 space-y-3 text-sm text-white/90">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#1d7ffd]" /> Verified hosts and inspected cars</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#1d7ffd]" /> Insurance on every booking</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#1d7ffd]" /> Pay by card or EFT, in ZAR</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const RoleCard = ({ active, onClick, title, desc, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={`text-left p-3.5 rounded-xl border transition ${active ? "border-[#1d7ffd] bg-[#1d7ffd]/5" : "border-[#E2E8F0] bg-white hover:border-[#08153a]"}`}>
    <p className="font-display font-bold text-[#08153a]">{title}</p>
    <p className="text-xs text-[#64748B]">{desc}</p>
  </button>
);
